import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { ActionButton, FormField, inputClass } from "../components/Primitives";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Confirm-password-reset page — hit at /reset-password/:token from the
 * email link. Two password fields (new + confirm), submits both to the
 * backend which single-uses the token, rotates the password, and returns
 * success. On success we redirect to /login rather than auto-signing in —
 * the extra login step means the new password actually gets verified
 * before the user gets any active session.
 */
export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (e2) {
      toast.error(errorMessage(e2, "Reset failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-8">
      <div className="max-w-md w-full border border-[#27272A] bg-[#121212]" data-testid="reset-password-page">
        <div className="border-b border-[#27272A] px-8 py-6">
          <div className="flex items-center gap-3 mb-4">
            {done ? <ShieldCheck className="size-6 text-[#34C759]" /> : <KeyRound className="size-6 text-[#FF3B30]" />}
            <div className="label-mute">Iris Security · Password Reset</div>
          </div>
          <div className="font-display text-2xl uppercase font-bold tracking-tight">
            {done ? "Password updated" : "Choose a new password"}
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            {done ? "Redirecting to login…" : "The link is single-use and expires in 30 minutes."}
          </div>
        </div>
        {!done && (
          <form onSubmit={submit} className="p-8 space-y-5">
            <p className="text-xs text-zinc-500 leading-relaxed">
              Minimum 8 characters. Once set, all currently signed-in sessions for this account will need to log in again.
            </p>
            <FormField label="New password">
              <input
                type="password"
                data-testid="reset-new-password-input"
                className={inputClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="new-password"
              />
            </FormField>
            <FormField label="Confirm new password">
              <input
                type="password"
                data-testid="reset-confirm-password-input"
                className={inputClass}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <ActionButton type="submit" disabled={submitting} data-testid="reset-submit-button">
              {submitting ? "Saving…" : "Update password"}
            </ActionButton>
            <div className="text-center pt-2">
              <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white">
                ← Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
